import React, { MouseEventHandler, useRef } from 'react';

import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';
import { getIconByName } from '../../../MuiIcon/Icon';
import { defaultIconSize, defaultIconSpacing } from '../../../../util/constants';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';
import { getBorderStyle, getDropShadowStyle, getWidgetOpacity } from '../util';
import { useWidgetAnimation } from '../../../Player/hooks/useWidgetAnimation';

const generateShapeContentStyle = (clickable?: boolean) => {
    // Grid stacking (NOT position:absolute): a CSS `position` on HTML content inside a
    // foreignObject mis-paints on Safari. A single-cell grid overlays the shape layer
    // and the icon with pure layout - no compositing layer - so it paints correctly.
    return {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
        width: '100%',
        height: '100%',
        // Pointer only when the widget has a linked event, like every other widget
        // (the overlay sets `cursor: pointer` on all children by default).
        cursor: clickable ? 'pointer' : 'default',
    } as React.CSSProperties;
};

const generateShapeWrapperVisualStyle = ({
    shapeType,
    style,
    isHover,
    clickable,
}: {
    shapeType?: string;
    style: StyleProps;
    isHover: boolean;
    clickable?: boolean;
}) => {
    const {
        transformSettings: { borderRadiusBottom, borderRadiusLeft, borderRadiusRight, borderRadiusTop },
        backgroundSettings: {
            backgroundColor,
            hoverBackgroundColor,
        },
    } = style;

    const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);

    const baseStyle = {
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        width: '100%',
        height: '100%',
        backgroundColor: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
        border: isHover ? (hoverBorder || border) : border,
    } as React.CSSProperties;

    switch (shapeType) {
        case 'circle':
            return { ...baseStyle, borderRadius: '50%' };
        case 'triangle':
            return { ...baseStyle, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' };
        case 'rectangle':
        default:
            return {
                ...baseStyle,
                borderTopRightRadius: `${borderRadiusTop}px`,
                borderBottomRightRadius: `${borderRadiusRight}px`,
                borderBottomLeftRadius: `${borderRadiusBottom}px`,
                borderTopLeftRadius: `${borderRadiusLeft}px`,
            };
    }
};

// Triangle is rendered as a real SVG <polygon>, NOT a clip-path div: a CSS `clip-path`
// on HTML content inside a foreignObject promotes it to a compositing layer that Safari
// positions in the wrong coordinate space (same family as transform/opacity/position -
// only triangle broke; circle uses border-radius, square uses border-radii). The polygon
// paints in place. fill/stroke are set on the <svg> and inherited by the polygon, so the
// press colour-flash (which writes `fill` on an <svg> bgElementRef) still works.
const generateTriangleStyle = ({
    style,
    isHover,
}: {
    style: StyleProps;
    isHover: boolean;
}) => {
    const {
        backgroundSettings: { backgroundColor, hoverBackgroundColor, borderColor, hoverBorderColor, borderWidth, hoverBorderWidth },
    } = style;
    const strokeWidth = isHover ? (hoverBorderWidth ?? borderWidth) : borderWidth;
    return {
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        width: '100%',
        height: '100%',
        fill: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
        stroke: isHover ? (hoverBorderColor || borderColor) : borderColor,
        strokeWidth: strokeWidth || 0,
    } as React.CSSProperties;
};

const generateWrapperStyle = ({
    style,
    isHover,
    clickable,
}: {
    style: StyleProps;
    isHover: boolean;
    clickable?: boolean;
}) => {
    const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);
    return {
        filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
    } as React.CSSProperties;
};

const generateIconContainerStyle = ({ spacing }: { spacing: string }) => {
    return {
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        placeSelf: 'center',
        paddingBottom: `${spacing}px`,
        paddingLeft: `${spacing}px`,
        paddingRight: `${spacing}px`,
        paddingTop: `${spacing}px`,
        lineHeight: 0,
    } as React.CSSProperties;
};

const generateIconStyle = ({ isHover, size, color, hoverColor }: { isHover: boolean; size: string; color: string; hoverColor: string }) => {
    return {
        fontSize: `${size}px`,
        color: isHover ? (hoverColor || color) : color,
    };
};

const Shape = ({
    id,
    style,
    data,
    clickable,
    isGoToSceneClick,
    isHover,
    scenarioId,
    sceneId,
    widgetRef,
    isShowWidget,
    isExiting,
    onExitDone,
    exitTiming,
    onClickExitStart,
    onClick,
    handleMouseEnter,
    handleMouseLeave,
}: DetailWidgetInteractionItem) => {
    const {
        textLabel: { color, hoverColor },
        coordinate: { x, y },
        dimension: { w = 150, h = 100 },
        rotation,
        iconConfig: { size = defaultIconSize, spacing = defaultIconSpacing } = {},
    } = style;

    const {
        settings: { iconKey, shapeType },
        name,
        type,
    } = data;

    const icon =
        iconKey &&
        getIconByName(iconKey, {
            style: generateIconStyle({ isHover, size, color, hoverColor }),
        });

    const gElementRef = useRef<SVGGElement | null>();
    const iconElementRef = useRef<HTMLDivElement | null>();
    const bgElementRef = useRef<HTMLDivElement | SVGSVGElement | null>();
    const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, iconElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, exitTiming, onClickExitStart, clickable });

    const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        event.persist?.();
        sendInteractGATrackEventData(type, name, scenarioId, sceneId);
        triggerClickAnimation(() => onClick(event));
    };

    const handleOnKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            handleWidgetAction(event);
        }
    }

    return (<g style={{ transform: `rotate(${rotation ?? 0}deg)`, opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
        <g
            id={id}
            onClick={handleWidgetAction}
            onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
            onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
            onKeyDown={handleOnKeyDown}
            tabIndex={0}
            role="button"
            style={generateWrapperStyle({ style, isHover, clickable })}
            ref={r => gElementRef.current = r}
        >
            <foreignObject
                aria-hidden="true"
                ref={widgetRef}
                x={x}
                y={y}
                width={w}
                height={h}
            >
                <div style={generateShapeContentStyle(clickable)} aria-hidden="true">
                    {shapeType === 'triangle' ? (
                        <svg
                            ref={r => bgElementRef.current = r}
                            width="100%"
                            height="100%"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            style={generateTriangleStyle({ style, isHover })}
                        >
                            <polygon points="50,0 0,100 100,100" vectorEffect="non-scaling-stroke" />
                        </svg>
                    ) : (
                        <div ref={r => bgElementRef.current = r} style={generateShapeWrapperVisualStyle({ shapeType, style, isHover, clickable })} />
                    )}
                    {iconKey && (
                        <div ref={r => iconElementRef.current = r} style={generateIconContainerStyle({ spacing })}>{icon}</div>
                    )}
                </div>
            </foreignObject>
        </g>
    </g>);
};

export default Shape;
